import {
  Cartesian3,
  Color,
  createGuid,
  EllipsoidTerrainProvider,
  IntersectionTests,
  Ray,
} from "@cesium/engine";
import Utils from "../basic/Utils";
import DitapViewer from "../basic/DitapViewer";

/**
 * 지면 거리 계산 및 영역 표시 클래스.<br> 
 * Ground distance calculation and area display class.

 * @alias MeasurementLengthGround
 * @constructor
 *
 * @param {object} options 아래 속성 포함 객체.</br>Object with the following properties:
 * @param {DitapViewer} options.viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {Cartesian3[]} [options.positions] 지면 거리 측정을 위한 Cartesian 좌표 배열.</br> Cartesian coordinate array for measuring ground distances.</br>
 * ※ 주의사항(Note).</br>
 * 지형모델이 Ellipsoid(구형)인 경우, 생성자 함수에 측정 위치 입력으로 인스턴스 생성 가능.</br>
 * When the terrain model is Ellipsoid, it is possible to create an instance by providing a measurement location as input to the constructor function.</br>
 * 지형모델이 Terrain(실제 지형)인 경우 생성자 함수에 측정 위치 입력으로 인스턴스 생성 불가하며, InteractionLengthGround를 통해 실제 지형 모델 상에 생성해야함.</br>
 * When the terrain model is Terrain, it is not possible to create an instance by providing a measurement location as input to the constructor function. </br>
 * Instead, it must be placed on the actual terrain model using InteractionLengthGround.
  * @param {String} [options.labelOption = "total"]
 * 지면 거리 측정 완료 후 라벨 표시 옵션. 지정하지 않은 경우 개별 표시.</br> 
 * "total" : 폴리라인 전체 길이 표시. </br> 
 * "single" : 폴리라인 개별 길이 표시. </br></br>
 *
 * Label display option after completing the ground distance measurement. If not specified, display individually.</br>
  "total" : Labels displaying the full length of the polyline.</br>
  "single" : Labels showing the length for each polyline.
 * @param {number} [options.lerpCount] 포인트와 포인트 사이의 선형 보간할 개수(1~100). 지정하지 않은 경우 두 점 사이의 간격에 따라 선형 보간 계산(권장). (10m 이내: 50cm 간격, 100m 이내: 1m 간격, 1000m 이내: 10m 간격, 그 이상은 100m 간격)</br> 
 * The number of points to linearly interpolate between two(1 to 100). If not specified, linear interpolation is calculated automatically(recommended). (Within 10m: 50cm intervals, within 100m: 1m intervals, within 1000m: 10m intervals, beyond that: 100m intervals)
 * @param {Color} [options.entityColor = Color.fromBytes(255, 115, 0, 255)] 엔티티 색상.<br> Color of the entity.
 * 
 * @example
 * // 샌드캐슬 예제 'DiTAP - Ground Length Measurement' 참조
 * 
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * // 1. 지정된 영역 생성. (InteractionLengthGround를 통한 수정 불가.)
 * // ※ 주의사항(Note). 
 * // - 지형모델이 Ellipsoid(구형)인 경우, 생성자 함수에 측정 위치 입력으로 인스턴스 생성 가능.
 * // - When the terrain model is Ellipsoid, it is possible to create an instance by providing a measurement location as input to the constructor function.
 * // - 지형모델이 Terrain(실제 지형)인 경우 생성자 함수에 측정 위치 입력으로 인스턴스 생성 불가하며, InteractionLengthGround를 통해 실제 지형 모델 상에 생성해야함.
 * // - When the terrain model is Terrain (real terrain), it is not possible to create an instance by providing a measurement location as input to the constructor function.
 * // Instead, it must be placed on the actual terrain model using InteractionLengthGround.
 * 
 * const position1 = new Ditap.Cartesian3(
 *     -3044770.8581131194,
 *     4043787.775061581,
 *     3867425.490573394
 * );
 * const position2 = new Ditap.Cartesian3(
 *      -3044776.871333837,
 *      4043767.978344368,
 *      3867441.348968651
 * );
 * const position3 = new Ditap.Cartesian3(
 *      -3044797.958027237,
 *      4043766.8311267258,
 *      3867426.050280055
 * );
 * const positions = [position1, position2, position3];
 * 
 * const options = {
 *  viewer: viewer,
 *  positions: positions,
 *  labelOption: "total",
 *  lerpCount: 10,
 *  entityColor: Color.fromBytes(255, 115, 0, 255)
 * };
 *
 * const measurementLengthGround = new Ditap.MeasurementLengthGround(options);
 * 
 * // 2. 마우스로 영역 지정 후 생성. (InteractionLengthGround를 통해 지면 거리 측정 및 영역 표시.)
 * const measurementLengthGround = new Ditap.MeasurementLengthGround({
 *  viewer: viewer
 * });
 *
 * const interactionLengthGround = new Ditap.InteractionLengthGround();
 * interactionLengthGround.measurementLengthGround = measurementLengthGround;
 */
class MeasurementLengthGround {
  constructor(options) {
    const viewer = options.viewer;
    const positions = options.positions;
    const labelOption = options.labelOption;
    const lerpCount = options.lerpCount;
    const entityColor = options.entityColor;

    this._id = `MeasurementLengthGround${createGuid()}`;
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
      viewer.ditapStorage.measurementLengthGround[this.id] = this;
    } else {
      console.error("Type doesn't match");
    }
    this._centerPosition = undefined;
    this._pointEntities = [];
    this._labelEntities = [];
    this._polylineEntities = [];
    this._length = 0;
    if (labelOption === "total") {
      this._isLabelOption = labelOption;
    } else {
      this._isLabelOption = "single";
    }
    if (!isNaN(lerpCount)) {
      if (lerpCount > 0 && lerpCount <= 100) {
        this._lerpCount = Math.round(lerpCount);
      } else {
        this._lerpCount = "auto";
      }
    } else {
      this._lerpCount = "auto";
    }
    if (entityColor instanceof Color) {
      this._entityColor = entityColor;
    } else {
      this._entityColor = Color.fromBytes(255, 115, 0, 255);
    }
    this._labelHeight = undefined;
    this._windowResizeEvent = undefined;
    this._entityNum = 0;
    this._displayCondition = 2000;
    this._labelPositions = [];
    if (positions) {
      this._positions = positions;
      let isEllipsoid = false;
      if (viewer.terrainProvider instanceof EllipsoidTerrainProvider) {
        isEllipsoid = true;
      }
      if (isEllipsoid) {
        this.createEntity();
      }
    } else {
      this._positions = [];
    }
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  set viewer(viewer) {
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
    }
  }
  get viewer() {
    return this._viewer;
  }

  /*
   * 측정 객체 ID. </br>
   * ID to assign to the measurement object.
   * @type {string}
   */
  set id(id) {
    this._id = id;
  }
  get id() {
    return this._id;
  }

  /**
   * 지면 거리 측정 할 Cartesian 배열</br>
   * Cartesian array for measuring ground distances.
   * @type {Cartesian3[]}
   * @readonly
   */
  set positions(positions) {
    this._positions = positions;
  }
  get positions() {
    return this._positions;
  }

  /*
   * Polyline Entity의 중심 Cartesian 값.</br>
   * Center cartesians of the polylineEntity.
   * @type {Cartesian3}
   */
  set centerPosition(centerPosition) {
    this._centerPosition = centerPosition;
  }
  get centerPosition() {
    return this._centerPosition;
  }

  /**
   * 지면 거리 측정으로 얻은 Point Entity 배열.</br>
   * Array of point entities resulting from ground length measurements.
   * @type {Entity[]}
   * @readonly
   */
  set pointEntities(pointEntities) {
    this._pointEntities = pointEntities;
  }
  get pointEntities() {
    return this._pointEntities;
  }

  /**
   * 지면 거리 측정으로 얻은 Polyline Entity 배열.</br>
   * Array of polyline entities resulting from ground length measurements.
   * @type {Entity[]}
   * @readonly
   */
  set polylineEntities(polylineEntities) {
    this._polylineEntities = polylineEntities;
  }
  get polylineEntities() {
    return this._polylineEntities;
  }

  /*
   * 지면 거리 측정으로 얻은 Label Entity 배열.</br>
   * Array of label elements resulting from ground length measurements.
   * @type {Element[]}
   */
  set labelEntities(labelEntities) {
    this._labelEntities = labelEntities;
  }
  get labelEntities() {
    return this._labelEntities;
  }

  /**
   * 측정된 지면 거리 값.</br>
   * Measured ground length value.
   * @type {number}
   * @readonly
   */
  set length(length) {
    this._length = length;
  }
  get length() {
    return this._length;
  }

  /**
   * 지면 길이 측정 후 표시할 라벨 옵션. </br>
   * "total" : 폴리라인 전체 길이 표시. </br>
   * "single" : 폴리라인 개별 길이 표시. </br></br>
   * Options for labels to be displayed after ground length measurements.</br>
   * "total" : Labels displaying the full length of the polyline.</br>
   * "single" : Labels showing the length for each polyline.</br></br>
   *  ※ 주의사항(Note). </br>
   * 측정을 시작하기 전에 옵션 값을 변경하여야 반영. 측정이 완료된 경우 옵션 값 변경 시 반영되지 않음.</br>
   * Options must be changed before starting the measurement to take effect. Changes to options are not reflected once the measurement is completed.
   * @type {string}
   */
  set isLabelOption(isLabelOption) {
    this._isLabelOption = isLabelOption;
  }
  get isLabelOption() {
    return this._isLabelOption;
  }

  /*
   * 라벨 내부 높이. </br>
   * Internal height of the label element.
   * @type {number}
   */
  set labelHeight(labelHeight) {
    this._labelHeight = labelHeight;
  }
  get labelHeight() {
    return this._labelHeight;
  }

  /**
   * 포인트와 포인트 사이의 선형 보간할 개수(1~100). 자동으로 계산되기를 원하면 undefined 입력.</br>
   * The number of linear interpolations between points (1 to 100). If you want it to be automatically calculated, enter undefined.</br></br>
   * ※ 주의사항(Note). </br>
   * 측정을 시작하기 전에 변경하여야 반영. 측정이 완료된 경우 반영되지 않음.</br>
   * Changes must be made before starting the measurement to take effect. They will not be reflected once the measurement is completed.
   * @type {number | undefined}
   */
  set lerpCount(count) {
    if (!isNaN(count)) {
      if (count > 0 && count <= 100) {
        this._lerpCount = Math.round(count);
      } else {
        this._lerpCount = "auto";
      }
    } else {
      this._lerpCount = "auto";
    }
  }
  get lerpCount() {
    return this._lerpCount;
  }

  /*
   * 윈도우 크기 조절 시 실행 이벤트.</br>
   * Events to run when window resizing.
   * @type {Function}
   */
  set windowResizeEvent(windowResizeEvent) {
    this._windowResizeEvent = windowResizeEvent;
  }
  get windowResizeEvent() {
    return this._windowResizeEvent;
  }

  /*
   * 카메라 시점 변경 시 실행 이벤트. </br>
   * Events to run when changing camera viewpoint in window.
   * @type {Function}
   */
  set cameraChangedEvent(cameraChangedEvent) {
    this._cameraChangedEvent = cameraChangedEvent;
  }
  get cameraChangedEvent() {
    return this._cameraChangedEvent;
  }

  /*
   * 엔티티 번호. </br>
   * Number of entities.
   * @type {number}
   */
  set entityNum(entityNum) {
    this._entityNum = entityNum;
  }
  get entityNum() {
    return this._entityNum;
  }

  /*
   * 엔티티의 DistanceDisplayCondition 값.</br>
   * DistanceDisplayCondition value of entity.
   * @type {number}
   */
  set displayCondition(displayCondition) {
    this._displayCondition = displayCondition;
  }
  get displayCondition() {
    return this._displayCondition;
  }

  /**
   * 라벨 엔티티 위치.<br>
   * Label Entity Positions
   * @type {Cartesian3[]}
   */
  set labelPositions(labelPositions) {
    this._labelPositions = labelPositions;
  }
  get labelPositions() {
    return this._labelPositions;
  }

  /**
   * 엔티티 색상.<br>
   * Color of the entity.
   */
  set entityColor(entityColor) {
    if (entityColor instanceof Color) {
      this._entityColor = entityColor;
      Utils.changePointColor(this.pointEntities, entityColor);
      Utils.changePolylineColor(this.polylineEntities, entityColor);
    }
  }
  get entityColor() {
    return this._entityColor;
  }

  /**
   * 배열의 마지막 요소 제거. </br>
   * Remove last element from array.
   * @param {Entity[]} array 요소를 제거할 배열.</br> Array to remove last element.
   */
  removeLastElement(array) {
    array.pop();
  }

  /*
   * Ditap Viewer에서 엔티티 제거.</br>
   * Remove entity from viewer.
   * @param {Entity} entity 제거할 엔티티.</br> Entities to remove.
   */
  removeViewerEntity(entity) {
    this.viewer.entities.remove(entity);
  }

  /*
   * 측정 완료 후 생성된 모든 Point Entity 제거.</br>
   * Remove all point entities created after measurement completion and initialize arrays.
   */
  removePointEntities() {
    for (const entity of this.pointEntities) {
      this.viewer.entities.remove(entity);
    }
    this.pointEntities = [];
  }

  /*
   * 측정 완료 후 생성된 모든 Polyline Entity 제거.</br>
   * Remove all polyline entities created after measurement completion and initialize arrays.
   */
  removePolylineEntities() {
    for (const entity of this.polylineEntities) {
      this.viewer.entities.remove(entity);
    }
    this.polylineEntities = [];
  }

  /*
   * 측정 완료 후 생성된 모든 Label Entity 제거.</br>
   * Remove all label entities created after measurement completion and initialize arrays.
   */
  removeLabelEntities() {
    for (const label of this.labelEntities) {
      label.remove();
    }
    this.labelEntities = [];
  }

  /*
   * 측정 완료 후 생성된 모든 엔티티 제거.</br>
   * Remove all entities created after measurement completion and initialize arrays.
   */
  removeAllEntities() {
    this.removePointEntities();
    this.removePolylineEntities();
    this.removeLabelEntities();
  }

  /**
   * 인스턴스 파괴. 영구적으로 제거하는 경우 호출.</br>
   * Destroys the instance. Should be called if permanently removing the instance from layout.
   */
  destroy() {
    this.removeAllEntities();

    if (this.labelEntities && this.labelEntities.length > 0) {
      this.labelEntities.forEach((label) => {
        if (label instanceof Element) {
          label.removeEventListener("mouseover", Utils.hideLabelElement);
          label.removeEventListener("mouseover", Utils.showLabelElement);
        }
      });
    }

    // viewerStorage에서 삭제
    this.viewer.ditapStorage.removeInstanceById(this.id);

    this.removeChangeEvent();

    this.id = undefined;
    this.positions = [];
    this.centerPosition = undefined;
    this.drawingLabelEntities = [];
    this.drawingPolylineEntities = [];
    this.pointEntities = [];
    this.labelEntities = [];
    this.polylineEntities = [];
    this.length = 0;
    this.isLabelOption = "total";
    this.lerpCount = "auto";
    this.labelHeight = undefined;
    this.windowResizeEvent = undefined;
    this.resizeObserver = undefined;
    this.entityNum = 0;
    this.labelPositions = [];
  }

  /*
   * 현재 거리에 매개변수 값을 더하여 전체 거리 계산. <br>
   * Add the parameter value to the current distance to calculate the total distance.
   * @param {number} length 추가할 거리.</br> length to add.
   * @returns  {number} 측정된 거리.</br> Calculated length.
   */
  plusLength(length) {
    const totalLength = this.length + length;
    return Number(totalLength.toFixed(2));
  }

  /*
   * Cartesian 좌표 배열을 사용하여 선형 보간 계산.</br>
   * Computes the linear interpolation or extrapolation at t using the provided cartesians.
   * @returns {Cartesian3[]} 선형 보간 할 Cartesian 배열.</br> Calculated Cartesian Array.
   */
  makeLerpCartesian() {
    const positions = this.positions;

    // 총 길이에 따라 lerp 간격 정하기
    let totalDistance = 0;
    for (let i = 0; i < positions.length - 1; i++) {
      totalDistance += Cartesian3.distance(positions[i], positions[i + 1]);
    }
    let lerpDistance;
    if (totalDistance <= 10) {
      lerpDistance = 0.5;
    } else if (totalDistance <= 100) {
      lerpDistance = 1;
    } else if (totalDistance <= 1000) {
      lerpDistance = 10;
    } else {
      lerpDistance = 100;
    }

    // 표면거리에 따라 lerp 간격 조절
    const lerpList = [];
    for (let i = 0; i < positions.length - 1; i++) {
      const distance = Cartesian3.distance(positions[i], positions[i + 1]);
      let count;

      if (this.lerpCount === "auto") {
        count = Math.max(2, Math.ceil(distance / lerpDistance));
      } else {
        count = this.lerpCount;
      }

      const array = [];
      for (let j = 0; j < count; ++j) {
        const offset = j / (count - 1);
        array.push(
          Cartesian3.lerp(
            positions[i],
            positions[i + 1],
            offset,
            new Cartesian3()
          )
        );
      }
      lerpList.push(array);
    }
    return lerpList;
  }

  /*
   * 지면 거리 측정 후 엔티티 생성.</br>
   * Create entities after measuring ground length.
   * @param {Function} [callback] The callback function.
   */
  createEntity(callback) {
    const viewer = this.viewer;

    const displayCondition = Utils.getDistanceDisplayConditon(this.positions);
    if (displayCondition > 2000) {
      this.displayCondition = displayCondition;
    }

    const lerpMultiList = this.makeLerpCartesian();

    const lerpArray = [];
    const copyArray = [];
    for (const list of lerpMultiList) {
      lerpArray.push(...list);
      copyArray.push(...list);
    }

    const promise = viewer.scene.clampToHeightMostDetailed(
      lerpArray,
      viewer.entities.values
    );

    promise
      .then((clampedArray) => {
        const array = [];
        for (let i = 0; i < clampedArray.length; i++) {
          if (clampedArray[i]) {
            array.push(clampedArray[i]);
          } else {
            const direction = new Cartesian3.multiplyByScalar(
              copyArray[i],
              -1,
              new Cartesian3()
            );

            const ray = new Ray(copyArray[i], direction);
            const intersection = IntersectionTests.rayEllipsoid(
              ray,
              viewer.scene.globe.ellipsoid
            );
            array.push(Ray.getPoint(ray, intersection.start));
          }
        }
        return array;
      })
      .then((array) => {
        this.drawingEntity(array, lerpMultiList);
      });

    if (callback) {
      callback();
    }
  }

  /*
   * Ditap Viewer에 Point, Polyline, Label 엔티티 생성.</br>
   * Create points, polylines, and labels in the viewer
   * @param {Cartesian3} cartesians 생성할 엔티티 좌표.</br> Cartesian coordinate arrangement to create the entity
   * @param {Cartesian3[]} lerpMultiList 개별 라벨을 생성하기 위한 Cartesian 배열. </br> Cartesian coordinate arrangement for generating individual labels
   */
  drawingEntity(cartesians, lerpMultiList) {
    // 라벨 옵션에 따라 라벨 생성
    this.createLabelByOption(cartesians, lerpMultiList);

    for (let k = 0; k < cartesians.length; k++) {
      const pointOPtion = {
        viewer: this.viewer,
        id: this.id + this.entityNum,
        position: cartesians[k],
        outlineColor: this.entityColor,
        displayCondition: this.displayCondition,
      };
      this.entityNum++;

      const point = Utils.createPoint(pointOPtion);
      this.pointEntities.push(point);
    }

    const polylineOption = {
      viewer: this.viewer,
      id: this.id + this.entityNum,
      positions: cartesians,
      material: this.entityColor,
      displayCondition: this.displayCondition,
    };
    const polyline = Utils.createPolyline(polylineOption);
    this.polylineEntities.push(polyline);

    if (this.cameraChangedEvent === undefined) {
      this.createCameraMoveEvent();
    }
    if (this.windowResizeEvent === undefined) {
      this.createWindowResizeEvent();
    }
  }

  /**
   * 옵션에 따른 라벨 생성. <br>
   * Create labels based on options.
   * @param {Cartesian3} cartesians 전체 라벨을 생성하기 위한 Cartesian 좌표.</br>  Cartesian coordinate arrangement to generate the entire label
   * @param {Cartesian3[]} lerpMultiList 개별 라벨을 생성하기 위한 Cartesian 배열. </br> Cartesian coordinate arrangement for generating individual labels
   */
  createLabelByOption(cartesians, lerpMultiList) {
    if (this.isLabelOption === "single") {
      this.length = 0;
      let totalLength = 0;
      const label = Utils.createLabel(this.viewer, length, totalLength);
      this.labelEntities.push(label);
      this.labelHeight = label.clientHeight;
      Utils.changeLabelLocation(
        this.viewer,
        cartesians[0],
        label,
        this.labelHeight
      );
      this.labelPositions.push(cartesians[0]);

      let startIndex;

      for (let i = 0; i < lerpMultiList.length; i++) {
        let length = 0;

        if (i === 0) {
          startIndex = 0;
        } else {
          startIndex = startIndex + lerpMultiList[i - 1].length;
        }
        for (let j = 0; j < lerpMultiList[i].length - 1; j++) {
          length =
            length +
            Cartesian3.distance(
              cartesians[startIndex + j],
              cartesians[startIndex + j + 1]
            );
        }
        length = Number(length.toFixed(2));
        totalLength = this.plusLength(length);
        this.length = totalLength;
        const label = Utils.createLabel(this.viewer, length, totalLength);
        this.labelEntities.push(label);
        this.labelHeight = label.clientHeight;
        Utils.changeLabelLocation(
          this.viewer,
          cartesians[startIndex + lerpMultiList[i].length - 1],
          label,
          this.labelHeight
        );
        this.labelPositions.push(
          cartesians[startIndex + lerpMultiList[i].length - 1]
        );
      }
    } else {
      let length = 0;
      for (let i = 0; i < cartesians.length - 1; i++) {
        length = length + Cartesian3.distance(cartesians[i], cartesians[i + 1]);
      }
      length = Number(length.toFixed(2));
      this.length = length;

      const arrayLength = cartesians.length;
      let centerIndex;

      if (arrayLength % 2 === 0) {
        centerIndex = arrayLength / 2;
      } else {
        centerIndex = Math.round(arrayLength / 2);
      }

      const label = Utils.createTotalLabel(this.viewer, this.length);
      this.labelHeight = label.clientHeight;
      this.centerPosition = cartesians[centerIndex];
      Utils.changeLabelLocation(
        this.viewer,
        cartesians[centerIndex],
        label,
        this.labelHeight
      );
      this.labelEntities.push(label);
    }
  }

  /*
   * 윈도우 크기 조절 이벤트 제거.</br>
   * Create the window resize event.
   */
  removeChangeEvent() {
    if (this.cameraChangedEvent) {
      this.cameraChangedEvent();
    }

    if (this.windowResizeEvent) {
      window.removeEventListener("resize", this.windowResizeEvent);
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   */
  createWindowResizeEvent() {
    const container = this.viewer.container;

    const resizeOriginal = () => {
      this.changeLabelVisibilityByOption();
    };

    this.windowResizeEvent = () => {
      resizeOriginal();

      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.windowResizeEvent);

    // ResizeObserver 객체를 생성하고 콜백 함수 전달.
    this.resizeObserver = new ResizeObserver(resizeOriginal);

    // 대상 요소에 대한 크기 변화 관찰.
    this.resizeObserver.observe(container);
  }
  /*
   * 카메라 시점 이동 시 이벤트 생성. </br>
   * Create an event when moving the camera point of view.
   */
  createCameraMoveEvent() {
    const viewer = this.viewer;

    // 카메라 이동 이벤트 생성
    this.cameraChangedEvent = viewer.camera.changed.addEventListener(() => {
      this.changeLabelVisibilityByOption();
    });
  }

  /*
   * 라벨 옵션에 따라 라벨 가시성 변경 함수<br>
   * Change label visibility according to label options.
   */
  changeLabelVisibilityByOption() {
    const viewer = this.viewer;
    const labelEntities = this.labelEntities;
    const labelPositions = this.labelPositions;
    const positions = this.positions;
    const height = this.labelHeight;
    const displayCondition = this.displayCondition;

    if (this.isLabelOption === "total") {
      Utils.changeTotalLabelVisibility(
        viewer,
        labelEntities[0],
        this.centerPosition ?? positions[0],
        displayCondition,
        height
      );
    } else {
      Utils.changeLabelVisibility(
        viewer,
        labelEntities,
        labelPositions,
        displayCondition,
        height
      );
    }
  }
}

export default MeasurementLengthGround;
